/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { VARIANT_SIMPLE } from '../PriceDetails/constants'

import CheckoutWalletSelector from 'src/comps/CheckoutWalletSelector'

import Price from 'client/components/Price'
import MessageBox from 'src/comps/MessageBox'

import { Amount, Line, LineRoomResume, PriceDetailsBase, Title } from '../PriceDetails/styles'

import {
  LineTaxes,
  LineStaywallet,
  LineInsurance,
  LineAdditionalFees,
  LineTotalPrice,
  LineRoomsProps,
  PriceDetailsProps,
  LinePriceDropProtect,
} from '../PriceDetails'

import styled from 'styled-components'

interface LineRoomsRefinedProps extends LineRoomsProps {
  roomsNumber: number
}

const LineRoom = ({ nights, roomsNumber, subtotal, subtotalCurrency }: LineRoomsRefinedProps) => {
  const intl = useIntl()
  const valueNights = { nights, rooms: roomsNumber }
  const valuePricePerNighs = useMemo(
    () => ({
      price: <Price amount={Math.round(subtotal / nights)} currency={subtotalCurrency} />,
    }),
    [nights, subtotal, subtotalCurrency]
  )

  const roomsKey = roomsNumber > 1 ? 'global.rooms' : 'global.room'

  return nights ? (
    <Line data-testid="subtotal">
      <LineRoomResume>
        <p>
          {roomsNumber} {intl.formatMessage({ id: roomsKey })} x{' '}
          {intl.formatMessage({ id: 'global.nights' }, valueNights)}
        </p>
        <span>{intl.formatMessage({ id: 'global.price-night' }, valuePricePerNighs)}</span>
      </LineRoomResume>
      <Amount>
        <Price amount={subtotal} currency={subtotalCurrency} />
      </Amount>
    </Line>
  ) : null
}

export const LineTotalPriceRefined = styled(LineTotalPrice)``

export interface PriceDetailsRefinedProps extends PriceDetailsProps {
  roomsNumber: number
}

const PriceDetailsRefined = ({
  additionalFees,
  insurance,
  insuranceCurrency,
  pdp,
  pdpCurrency,
  isVoucher,
  nights,
  payDate,
  payLate,
  payNow,
  purchaseId,
  roomsNumber,
  sellingRateCurrency,
  showPaymentDates = true,
  showWalletCheckbox,
  staywallet,
  staywalletCurrency,
  subtotal,
  subtotalCurrency,
  taxes,
  taxesCurrency,
  total,
  totalCurrency,
  totalWithFees,
  variant,
}: PriceDetailsRefinedProps) => {
  const intl = useIntl()

  const dateText = intl.formatDate(payDate, { year: 'numeric', month: 'long', day: '2-digit' })
  const valuePayDate = { date: dateText }

  return (
    <PriceDetailsBase data-testid="price-details-refined">
      {variant !== VARIANT_SIMPLE && (
        <Title>
          {intl.formatMessage({
            id: isVoucher ? 'global.price' : 'global.price-breakdown',
          })}
        </Title>
      )}

      {showWalletCheckbox && purchaseId && <CheckoutWalletSelector purchaseId={purchaseId} />}

      <LineRoom
        roomsNumber={roomsNumber}
        nights={nights}
        subtotal={subtotal}
        subtotalCurrency={subtotalCurrency}
      />

      <LineTaxes taxes={taxes} taxesCurrency={taxesCurrency} />
      {!isVoucher ? (
        <LineAdditionalFees additionalFees={additionalFees} isVoucher={isVoucher} />
      ) : null}

      <LineStaywallet
        staywallet={staywallet}
        staywalletCurrency={staywalletCurrency}
        variant={variant}
      />
      <LineInsurance
        insurance={insurance}
        insuranceCurrency={insuranceCurrency}
        variant={variant}
      />

      <LinePriceDropProtect pdp={pdp} pdpCurrency={pdpCurrency} variant={variant} />

      <LineTotalPriceRefined total={totalWithFees ?? total} totalCurrency={totalCurrency} />

      {isVoucher ? (
        <LineAdditionalFees additionalFees={additionalFees} isVoucher={isVoucher} />
      ) : null}

      {variant !== VARIANT_SIMPLE && payNow >= 0 && payNow !== (totalWithFees ?? total) ? (
        <Line variant="pay">
          <p>{intl.formatMessage({ id: 'global.now-pay' })}</p>
          <Amount>
            <Price amount={payNow} currency={sellingRateCurrency} />
          </Amount>
        </Line>
      ) : null}

      {variant !== VARIANT_SIMPLE && showPaymentDates && payLate && payDate ? (
        <Line variant="pay">
          <p>{intl.formatMessage({ id: 'global.you-will-pay' }, valuePayDate)}</p>
          <Amount>
            <Price amount={payLate} currency={sellingRateCurrency} />
          </Amount>
        </Line>
      ) : null}

      {variant !== VARIANT_SIMPLE ? (
        <LineAdditionalFees additionalFees={additionalFees} isVoucher={isVoucher} simple />
      ) : null}

      <MessageBox message={intl.formatMessage({ id: 'global.currency-charge' })} type="info" />
    </PriceDetailsBase>
  )
}

export default PriceDetailsRefined
